import React from "react";
import { useTranslation } from "react-i18next";
import { Customer } from "../../../../models";
import { HiCheckCircle } from "react-icons/hi";
import { IOrder } from "../../../../types";

interface PublicOrderSuccessPageProps {
  order: IOrder;
  onClose?: () => void;
  config?: any;
}

const PublicOrderSuccessPage: React.FC<PublicOrderSuccessPageProps> = ({
  order,
  onClose,
  config,
}) => {
  const { t } = useTranslation(["order_form"]);

  const cssVariables = {
    "--public-form-primary": config?.primaryColor || "#7214FF",
    "--public-form-primary-light": config?.primaryColorLight || "#F1E8FF",
    "--public-form-primary-dark": config?.primaryColorDark || "#5B0ACD",
  };

  return (
    <div
      className="min-h-screen bg-gray-100 flex flex-col"
      style={cssVariables as React.CSSProperties}
    >
      {/* Fixed Logo Header */}
      {config?.logoUrl && config.logoUrl.trim() !== "" && (
        <div
          className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200"
          style={{ backgroundColor: config?.headerColor || "#FFFFFF" }}
        >
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex justify-start">
              {config.logoRedirectUrl &&
              config.logoRedirectUrl.trim() !== "" ? (
                <a href={config.logoRedirectUrl}>
                  <img
                    src={config.logoUrl}
                    alt="Company logo"
                    className="h-12 max-w-48 object-contain"
                    style={{ cursor: "pointer" }}
                  />
                </a>
              ) : (
                <img
                  src={config.logoUrl}
                  alt="Company logo"
                  className="h-12 max-w-48 object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex-1 flex items-center justify-center mt-8 pb-8 ${config?.logoUrl && config.logoUrl.trim() !== "" ? "pt-20" : ""}`}
      >
        <style>
          {`
                    .public-form-primary-text {
                        color: var(--public-form-primary);
                    }
                `}
        </style>
        <div className="max-w-7xl w-full px-4">
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div className="flex flex-col items-start text-left mb-8">
              <div className="bg-green-100 p-4 rounded-full mb-6">
                <HiCheckCircle className="text-green-500 h-16 w-16" />
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 mb-4">
                {config?.productListFlowConfirmationTitle ||
                  config?.customOrderFlowConfirmationTitle ||
                  t("public_form.success.title")}
              </h1>
              <p className="text-lg text-gray-600">
                {config?.productListFlowConfirmationDescription ||
                  config?.customOrderFlowConfirmationDescription ||
                  t("public_form.success.description")}
              </p>
            </div>

            <div className="space-y-6">
              {/* Order Summary Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">
                  {t("public_form.success.your_order")}
                </h3>

                {/* Route Information - Simplified */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {t("public_form.success.transport_route")}
                  </h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    {order.route && order.route.length > 0 ? (
                      order.route.map((stop, index) => (
                        <div key={index} className="flex">
                          <span className="text-gray-500 mr-2">
                            {stop.isPickup
                              ? t("public_form.common.pickup")
                              : t("public_form.common.delivery")}
                            :
                          </span>
                          <span>
                            {stop.location?.displayName ||
                              t("public_form.common.unknown_location")}
                            {(stop.location?.addressLine ||
                              stop.location?.postCode ||
                              stop.location?.city) && (
                              <span className="text-gray-500">
                                {" - "}
                                {stop.location?.addressLine &&
                                  stop.location.addressLine}
                                {(stop.location?.postCode ||
                                  stop.location?.city) && (
                                  <>
                                    {stop.location?.addressLine && ", "}
                                    {stop.location?.postCode &&
                                    stop.location?.city
                                      ? `${stop.location.postCode} ${stop.location.city}`
                                      : stop.location?.postCode ||
                                        stop.location?.city}
                                  </>
                                )}
                              </span>
                            )}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">
                        {t("public_form.success.no_route_info")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Cargo Information - Simplified */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {t("public_form.common.cargo")}
                  </h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    {order.cargo && order.cargo.length > 0 ? (
                      order.cargo.map((item, index) => (
                        <div key={index}>
                          <span>
                            {item.descriptionShort ||
                              t("public_form.common.unknown_product")}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({item.quantity} {t("public_form.common.pieces")},{" "}
                            {item.weight} kg)
                          </span>
                        </div>
                      ))
                    ) : order.comment && order.comment.trim() ? (
                      <div>
                        <p className="text-gray-600 mb-1">
                          {t("public_form.success.transport_needs_description")}
                          :
                        </p>
                        <p className="text-gray-900">{order.comment}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500">
                        {t("public_form.success.no_cargo_info")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Next Steps Section - Simplified */}
              <div className="pt-4 mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {t("public_form.success.what_happens_next")}
                </h4>
                <div className="text-sm text-gray-600 whitespace-pre-line">
                  {config?.productListFlowConfirmationNextSteps ||
                    config?.customOrderFlowConfirmationNextSteps ||
                    `1. ${t("public_form.success.next_step_1")}\n2. ${t("public_form.success.next_step_2")}\n3. ${t("public_form.success.next_step_3")}`}
                </div>
              </div>

              {/* Contact Information - Simplified */}
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {t("public_form.success.contact_information")}
                </h4>
                <div className="text-sm text-gray-600">
                  <div>
                    {Customer.getCustomerName(order.customer)}
                  </div>
                  <div>{order.customer?.contactEmail}</div>
                  <div>{order.customer?.phoneNumber}</div>
                </div>
              </div>

              {/* Website Button */}
              {config?.logoRedirectUrl &&
                config.logoRedirectUrl.trim() !== "" && (
                  <div className="flex justify-center">
                    <a
                      href={config.logoRedirectUrl}
                      className="inline-flex items-center px-8 py-4 text-lg font-medium text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                      style={{
                        backgroundColor: config?.primaryColor || "#7214FF",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          config?.primaryColorDark || "#5B0ACD";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          config?.primaryColor || "#7214FF";
                      }}
                    >
                      {t("public_form.success.to_website")}
                    </a>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicOrderSuccessPage;
